import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Filter, Plus, Settings2, Target } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { ContactAgent } from '@workspace/types';
import { leadsApi } from '../api/leadsApi';
import {
  LeadsFunnelLegacyBanner,
  type LeadsFunnelStatus,
} from '../components/LeadsFunnelLegacyBanner';
import { contactAgentError } from '../../contact-agent/api/contactAgentScope';
import { ContactAgentPage, ContactAgentPageHeader } from '../../contact-agent/components/ContactAgentPageHeader';
import { ContactAgentTenantPicker } from '../../contact-agent/components/ContactAgentTenantPicker';
import { useContactAgentTenant } from '../../contact-agent/hooks/useContactAgentTenant';

export function LeadsDashboardPage() {
  const { tenantId, tenants, isSuperAdmin, ready, selectTenant } = useContactAgentTenant();
  const [agents, setAgents] = useState<ContactAgent[]>([]);
  const [leadCount, setLeadCount] = useState(0);
  const [error, setError] = useState('');
  const [funnelStatus, setFunnelStatus] = useState<LeadsFunnelStatus | null>(null);

  useEffect(() => {
    if (!ready) {
      return;
    }
    let cancelled = false;
    setError('');
    void Promise.all([leadsApi.listAgents(), leadsApi.listLeads(), leadsApi.getFunnelStatus()])
      .then(([leadAgents, leads, status]) => {
        if (cancelled) {
          return;
        }
        setAgents(leadAgents);
        setLeadCount(leads.length);
        setFunnelStatus(status);
      })
      .catch(err => {
        if (!cancelled) {
          setError(contactAgentError(err));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [ready, tenantId]);

  return (
    <ContactAgentPage>
      <ContactAgentPageHeader
        title='Embudo de leads'
        description='Agentes de calificación comercial y tablero de oportunidades.'
        showBack={false}
        toolbar={
          isSuperAdmin ? (
            <ContactAgentTenantPicker tenants={tenants} tenantId={tenantId} onChange={selectTenant} />
          ) : null
        }
        actions={
          <div className='flex flex-wrap gap-2'>
            <Button variant='outline' asChild>
              <Link to='/admin/leads/funnel-config'>
                <Settings2 className='mr-2 h-4 w-4' />
                Configuración
              </Link>
            </Button>
            <Button variant='outline' asChild>
              <Link to='/admin/leads/board'>
                <Filter className='mr-2 h-4 w-4' />
                Ver leads ({leadCount})
              </Link>
            </Button>
            <Button asChild>
              <Link to='/admin/leads/agents/new'>
                <Plus className='mr-2 h-4 w-4' />
                Nuevo agente
              </Link>
            </Button>
          </div>
        }
      />

      <LeadsFunnelLegacyBanner status={funnelStatus} />

      {error ? <p className='text-sm text-destructive'>{error}</p> : null}

      <div className='grid gap-4 md:grid-cols-2 xl:grid-cols-3'>
        {agents.map(agent => (
          <Card key={agent.id} className='rounded-2xl shadow-sm'>
            <CardHeader className='flex flex-row items-start justify-between gap-2'>
              <div>
                <CardTitle className='text-lg'>{agent.name}</CardTitle>
                <p className='text-sm text-muted-foreground'>{agent.bot?.name ?? agent.botId}</p>
              </div>
              <Badge variant='secondary'>Leads</Badge>
            </CardHeader>
            <CardContent className='space-y-3'>
              <p className='text-sm text-muted-foreground line-clamp-2'>
                {agent.objective || 'Sin objetivo definido.'}
              </p>
              <Button variant='outline' size='sm' asChild>
                <Link to={`/admin/leads/agents/${agent.id}`}>Editar agente</Link>
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {ready && agents.length === 0 && !error ? (
        <Card className='rounded-2xl border-dashed'>
          <CardContent className='flex flex-col items-center gap-3 py-12 text-center'>
            <Target className='h-10 w-10 text-muted-foreground' />
            <p className='text-muted-foreground'>Todavía no hay agentes de embudo configurados.</p>
            <Button asChild>
              <Link to='/admin/leads/agents/new'>Crear primer agente</Link>
            </Button>
          </CardContent>
        </Card>
      ) : null}
    </ContactAgentPage>
  );
}
